import React from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Spinner.css';

/**
 * Spinner Component
 * Loading spinner with multiple sizes and variants
 *
 * @param {Object} props - Component props
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.variant - Visual variant: 'primary', 'secondary', 'light'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Accessible label for screen readers
 */
const Spinner = ({
  size = 'md',
  variant = 'primary',
  className = '',
  label = 'Loading',
}) => {
  const spinnerClasses = `
    spinner
    spinner--${size}
    spinner--${variant}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="spinner-wrapper" role="status" aria-label={label}>
      <motion.div
        className={spinnerClasses}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <svg viewBox="0 0 50 50" className="spinner-svg">
          <motion.circle
            className="spinner-circle"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
            initial={{ pathLength: 0.2 }}
            animate={{ pathLength: [0.2, 0.8, 0.2] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </svg>
      </motion.div>
      <span className="sr-only">{label}</span>
    </div>
  );
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'light']),
  className: PropTypes.string,
  label: PropTypes.string,
};

export default Spinner;
